import PolicyLayout, { H2, P, UL } from "./PolicyLayout";
import { useSettings } from "../../contexts/SettingsContext";

export default function TermsConditions() {
  // iter96 / Session C — the "Order cancellation" bullets below react
  // to the admin cancellation toggle.
  // iter101 — Section 7 "Payment processing" also reacts to the admin
  // COD toggle so we never advertise a payment method that is currently
  // disabled at checkout.  All other wording (usage, pricing, warranty,
  // shipping) is preserved verbatim.
  const { customerCancellationEnabled, codEnabled } = useSettings();
  return (
    <PolicyLayout
      title="Terms & Conditions"
      testid="page-terms-conditions"
      canonical="/terms-and-conditions"
      seoTitle="Terms & Conditions | Sri Durga Foods"
      seoDescription="Sri Durga Foods website terms of use, order acceptance, pricing, warranties, and user responsibilities."
    >
      <P>
        Welcome to Sri Durga Foods. These Terms &amp; Conditions govern your use
        of <strong>www.sridurgafoods.com</strong> (&ldquo;the Website&rdquo;) and the
        purchase of products through it. By browsing, registering an account,
        or placing an order you agree to be bound by these terms in full.
      </P>

      <H2>1. Website usage</H2>
      <P>
        You agree to use the Website only for lawful purposes and in a manner
        that does not infringe the rights of, or restrict or inhibit the use
        of, the Website by any third party. Sri Durga Foods may revise these
        Terms at any time. Continued use of the Website constitutes
        acceptance of the revised Terms.
      </P>

      <H2>2. User accounts</H2>
      <UL>
        <li>You must be at least 18 years old, or accessing the Website under the supervision of a parent or guardian, to create an account.</li>
        <li>You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account.</li>
        <li>You agree to provide accurate, current and complete information during registration and to update it as required.</li>
        <li>We may suspend or terminate accounts that violate these Terms, abuse the platform, or are involved in fraudulent activity.</li>
      </UL>

      <H2>3. Product information</H2>
      <P>
        We make every effort to display our products — pickles, culinary
        pastes, spices, masalas, traditional powders, flours and wood-pressed
        oils — as accurately as possible, including ingredients, weights and
        packaging. However, slight variations in colour, texture or batch
        quality may occur due to the natural, hand-crafted nature of our
        food. Such variations do not constitute a defect.
      </P>

      <H2>4. Pricing and availability</H2>
      <UL>
        <li>All prices are listed in Indian Rupees (INR / ₹) and are inclusive of applicable taxes unless stated otherwise.</li>
        <li>Prices and product availability may change without notice. We reserve the right to limit quantities purchased per customer or per order.</li>
        <li>In the rare event of a pricing error, we reserve the right to cancel the order and issue a full refund.</li>
      </UL>

      <H2>5. Order acceptance</H2>
      <P>
        Placing an order constitutes your offer to buy. An order is only
        accepted once we send an order-confirmation message or email. We
        reserve the right to refuse or cancel any order at our sole
        discretion, including (but not limited to) cases of suspected fraud,
        stock unavailability, incorrect pricing, or non-serviceable delivery
        locations.
      </P>

      <H2>6. Order cancellation</H2>
      {customerCancellationEnabled ? (
        <UL>
          <li>Customers may cancel their order any time before dispatch, free of charge, either directly from <strong>Your Orders → Order Details</strong> or by contacting us via WhatsApp, phone or email with their order ID.</li>
          <li>Once an order is handed to our shipping partner (Shiprocket) it can no longer be cancelled; please follow the Return &amp; Refund Policy if needed.</li>
          <li>We reserve the right to cancel orders post-acceptance if products become unavailable or the delivery address is non-serviceable; full refund will be issued in such cases.</li>
        </UL>
      ) : (
        <UL>
          <li>Self-service order cancellation is currently unavailable on the website. If you need to cancel a recent order, please contact us via WhatsApp, phone or email with your order ID and we will do our best to help before dispatch.</li>
          <li>Once an order is handed to our shipping partner (Shiprocket) it can no longer be cancelled; please follow the Return &amp; Refund Policy if needed.</li>
          <li>We reserve the right to cancel orders post-acceptance if products become unavailable or the delivery address is non-serviceable; full refund will be issued in such cases.</li>
        </UL>
      )}

      <H2>7. Payment processing (Razorpay)</H2>
      <P>
        Online payments on Sri Durga Foods are processed securely through{" "}
        <strong>Razorpay</strong>. We accept UPI (Google Pay, PhonePe, Paytm,
        CRED, BHIM), credit/debit cards, net-banking and select wallets. All
        payment information is handled directly by Razorpay; we do not store
        card numbers, CVV or UPI PINs on our servers.
        {codEnabled
          ? " Cash on Delivery (COD) is available for eligible pincodes."
          : " All orders must be paid online at checkout — Cash on Delivery is currently unavailable."}
      </P>

      <H2>8. Shipping (Shiprocket)</H2>
      <P>
        Orders are shipped via courier partners aggregated through{" "}
        <strong>Shiprocket</strong>. Shipping charges are{" "}
        <strong>calculated dynamically</strong> at checkout based on the
        destination pincode and the total shipment weight. Estimated
        delivery timelines are indicative and may vary due to courier
        operations, weather, or public holidays. Please refer to our
        Shipping Policy for full details.
      </P>

      <H2>9. Customer responsibilities</H2>
      <UL>
        <li>You agree to provide an accurate delivery address, contact number and pincode at checkout.</li>
        <li>Missed deliveries or RTOs (return-to-origin) due to incorrect addresses or unavailability may incur re-shipping charges.</li>
        <li>You agree to inspect the package on delivery and report damages/missing items within 24 hours.</li>
      </UL>

      <H2>10. Intellectual property</H2>
      <P>
        All content on the Website — including the Sri Durga Foods name,
        logo, product photography, descriptions, and recipes — is the
        intellectual property of Sri Durga Foods or its licensors and is
        protected by Indian copyright and trademark laws. You may not copy,
        reproduce or distribute any of this content without prior written
        consent.
      </P>

      <H2>11. Limitation of liability</H2>
      <P>
        To the maximum extent permitted by applicable Indian law, Sri Durga
        Foods shall not be liable for any indirect, incidental, special or
        consequential damages arising from the use of the Website or its
        products. Our total liability for any claim related to an order is
        limited to the amount paid for that specific order.
      </P>

      <H2>12. Governing law and jurisdiction</H2>
      <P>
        These Terms are governed by the laws of India. Any dispute arising
        out of or in connection with these Terms shall be subject to the
        exclusive jurisdiction of the competent courts in Nellore, Andhra
        Pradesh.
      </P>
    </PolicyLayout>
  );
}
